// All of a client's projects. One row each: reference and date, the title,
// where it stands, its total, and a line filled as far as the planning has
// got (the section indicator's language again). Hover: the others dim, the
// row's title slides right and an arrow springs in, as in the menu.
import * as React from "react"
import { Dots } from "../toolkit/Dots"
import { EASE, MS, linesIn, reduced, stagger } from "../toolkit/motion"
import { fmtDate, fmtMoney, type Loaded } from "./api"
import { Link, type Route } from "./route"
import { progress, status } from "./status"
import { t } from "./i18n"

export function Overview({ data, route, delay }: { data: Loaded; route: Route; delay: number }) {
    const root = React.useRef<HTMLElement>(null)

    React.useLayoutEffect(() => {
        const el = root.current!
        stagger(el.querySelectorAll("[data-r]"), { from: "up", distance: 28, gap: MS.stagger, delay })
        for (const t of el.querySelectorAll<HTMLElement>("[data-lines]")) linesIn(t, { delay })
        el.querySelectorAll<HTMLElement>("[data-draw]").forEach((l, i) =>
            l.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
                duration: reduced() ? 0 : MS.fill,
                delay: delay + 300 + i * MS.stagger,
                easing: EASE.natural,
                fill: "backwards",
            })
        )
    }, [])

    // in Tiago's order (set in the studio)
    const list = data.projects

    return (
        <main ref={root} className="p-page o-page">
            <section className="p-hero">
                <h1 className="p-title o-name" data-logo={data.logo ? "" : undefined}>
                    {data.logo && <img className="o-logo" src={data.logo} alt="" data-r />}
                    <span data-lines>{data.name}</span>
                </h1>
            </section>
            <section className="p-section o-section">
                <div className="p-section-label" data-r>
                    <h2 className="nto-label">{t("Projects")}</h2>
                    <span className="o-count nto-mono">
                        {data.projects.length} {data.projects.length === 1 ? t("project") : t("projects")}
                    </span>
                </div>
            <ul className="o-list">
                {list.map((e) => {
                    const s = status(e)
                    const p = e.project
                    return (
                        <li key={p.id} data-r>
                            <Link className="o-row" to={{ client: route.client, project: p.id, tab: "planning" }}>
                                <span className="o-ref nto-mono">
                                    <span>{p.reference}</span>
                                    <span>{fmtDate(p.date, true)}</span>
                                </span>
                                <span className="o-main">
                                    <span className="o-title">
                                        <span className="o-arrow">
                                            <Dots shape="arrow" />
                                        </span>
                                        <span className="o-title-text">{p.title}</span>
                                    </span>
                                    <span className="o-track" aria-hidden="true">
                                        <i />
                                        <span className="o-line" data-draw>
                                            <span style={{ transform: `scaleX(${progress(e)})` }} />
                                        </span>
                                        <i />
                                    </span>
                                </span>
                                <span className="o-side nto-mono">
                                    <span className="o-status" data-tone={s.tone}>
                                        {s.tone === "act" && <i />}
                                        {s.text}
                                    </span>
                                    <span className="o-total">{fmtMoney(e.totals.total, p.estimate.currency, false)}</span>
                                </span>
                            </Link>
                        </li>
                    )
                })}
            </ul>
            </section>
        </main>
    )
}
